/**
 * OrthophotoTicket — Orthophoto ticket content for EditorPanel.
 * Extracted from EditorPanel.tsx (refactoring, no behavior changes).
 */

import { useState, useRef } from 'react';
import { useProjectStore, useUIStore, useMapStore } from '../../../stores';
import { useIsReadOnlyViewer } from '../../../hooks/useShareViewer';
import { Button } from '../../common';
import {
  storeSourceFile, needsTileGeneration,
  exceedsServerPreviewLimits, FAST_DECODE_MAX_BYTES,
} from '../../../utils/sourceFiles';
import { regenerateTiles, requestPreview, requestTileGeneration } from '../../../services/tileApi';
import { uploadAsset, uploadLargeAsset } from '../../../services/assetApi';

/**
 * Placeholder shown while a large upload has no preview yet (the synchronous
 * Lambda preview is skipped above the SERVER_PREVIEW limits). data: URL — not
 * blob: — so it survives a save/reload; useTileStatusPoller swaps it for the
 * converter-generated COG preview once tiles are ready.
 */
function makePendingPlaceholder(width: number, height: number): string {
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = Math.max(64, Math.round(512 * (height / width)));
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#1a1a2e';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = '#e0e0e0';
  ctx.font = 'bold 24px sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('HD tiles pending...', canvas.width / 2, canvas.height / 2 - 10);
  ctx.font = '14px sans-serif';
  ctx.fillText(`${width}×${height} px`, canvas.width / 2, canvas.height / 2 + 20);
  return canvas.toDataURL('image/png');
}

export default function OrthophotoTicket() {
  const isReadOnly = useIsReadOnlyViewer();
  const meta = useProjectStore((s) => s.meta);
  const orthophotos = useProjectStore((s) => s.data.orthophotos);

  const addNotification = useUIStore((s) => s.addNotification);
  const showConfirm = useUIStore((s) => s.showConfirm);
  const setProgressOverlay = useUIStore((s) => s.setProgressOverlay);

  const activeOrthophotoId = useMapStore((s) => s.activeOrthophotoId);
  const orthoEditMode = useMapStore((s) => s.orthoEditMode);
  const mapInstance = useMapStore((s) => s.map);

  // File input refs
  const orthophotoInputRef = useRef<HTMLInputElement>(null);
  const manualImageInputRef = useRef<HTMLInputElement>(null);

  // Remove background checkbox state
  const [removeBgEnabled, setRemoveBgEnabled] = useState(true);

  // ============================================
  // ORTHOPHOTOS
  // ============================================
  const handleAddOrthophoto = () => { orthophotoInputRef.current?.click(); };
  const handleAddManualImage = () => { manualImageInputRef.current?.click(); };

  const processGeoTIFFFile = async (file: File) => {
    addNotification({ type: 'info', title: 'Processing GeoTIFF...', message: file.name });
    try {
      const { fromBlob } = await import('geotiff');
      const tiff = await fromBlob(file);
      const image = await tiff.getImage();
      const bbox = image.getBoundingBox();
      if (!bbox || bbox.length < 4) throw new Error('No geolocation data in GeoTIFF');
      const [minX, minY, maxX, maxY] = bbox;
      const origWidth = image.getWidth();
      const origHeight = image.getHeight();

      // 4 corners: TL, TR, BR, BL — in source CRS initially
      let corners: [[number, number], [number, number], [number, number], [number, number]] = [
        [minX, maxY], [maxX, maxY], [maxX, minY], [minX, minY],
      ];

      // Detect and reproject from projected CRS to WGS84 if needed
      const isProjected = Math.abs(minX) > 180 || Math.abs(maxX) > 180 || Math.abs(minY) > 90 || Math.abs(maxY) > 90;
      if (isProjected) {
        const geoKeys = image.getGeoKeys();
        const epsgCode = geoKeys.ProjectedCSTypeGeoKey || geoKeys.GeographicTypeGeoKey;
        if (epsgCode && epsgCode !== 32767) {
          addNotification({ type: 'info', title: `Reprojecting EPSG:${epsgCode}...`, message: 'Converting to WGS84' });
          try {
            const proj4 = (await import('proj4')).default;
            const resp = await fetch(`https://epsg.io/${epsgCode}.proj4`);
            if (!resp.ok) throw new Error(`Unknown EPSG:${epsgCode}`);
            let projStr = (await resp.text()).trim();
            // Some CRS definitions from epsg.io lack +towgs84 datum shift parameters,
            // causing visible offset (e.g. Israeli Grid 05/12 → ~50m shift)
            const KNOWN_TOWGS84: Record<number, string> = {
              2039: '-48,55,52,0,0,0,0',  // Israel 1993 / Israeli TM Grid
              6984: '-48,55,52,0,0,0,0',  // Israeli Grid 05/12
              6991: '-48,55,52,0,0,0,0',  // Israeli Grid 05/12 Intermediate CRS
            };
            if (!projStr.includes('+towgs84') && KNOWN_TOWGS84[epsgCode]) {
              projStr += ` +towgs84=${KNOWN_TOWGS84[epsgCode]}`;
              console.log('[GeoTIFF] Added missing +towgs84 for EPSG:', epsgCode);
            }
            console.log('[GeoTIFF] EPSG:', epsgCode, 'proj4:', projStr);
            console.log('[GeoTIFF] GeoKeys:', JSON.stringify(geoKeys));
            console.log('[GeoTIFF] Source bbox:', [minX, minY, maxX, maxY]);
            proj4.defs(`EPSG:${epsgCode}`, projStr);
            const fwd = proj4(`EPSG:${epsgCode}`, 'EPSG:4326');
            // Reproject all 4 corners independently to preserve rotation/skew
            corners = corners.map((c) => fwd.forward(c) as [number, number]) as typeof corners;
            console.log('[GeoTIFF] Reprojected corners:', corners);
            addNotification({ type: 'success', title: 'Reprojected to WGS84', message: `EPSG:${epsgCode} → WGS84` });
          } catch (projErr) {
            throw new Error(`Cannot reproject EPSG:${epsgCode}: ${projErr instanceof Error ? projErr.message : 'Unknown error'}`);
          }
        } else {
          throw new Error('Projected CRS detected but no EPSG code found in GeoTIFF metadata. Cannot reproject to WGS84.');
        }
      }
      const orthoName = file.name.replace(/\.(tif|tiff|geotiff)$/i, '');
      const orthoCaptureDate = useProjectStore.getState().selectedDate || undefined;
      // Compute axis-aligned bounds from (possibly rotated) corners
      const lngs = corners.map((c) => c[0]);
      const lats = corners.map((c) => c[1]);
      const boundsMinLng = Math.min(...lngs), boundsMaxLng = Math.max(...lngs);
      const boundsMinLat = Math.min(...lats), boundsMaxLat = Math.max(...lats);

      // ── Large GeoTIFFs default straight to HD — no chooser (owner decision
      // 2026-09-01). The HD/Fast popup predated the container preview: "HD"
      // used to mean minutes of waiting with no image, so "Fast" existed as
      // the quick-positioning escape. Now HD delivers a ~2000px preview in
      // seconds AND full tiles, strictly dominating Fast, so the popup was a
      // question with one sensible answer asked on every upload. This branch
      // sits BEFORE any raster decode on purpose: readRasters below allocates
      // gigabytes for huge sources (an 18 GB upload died there once), and the
      // HD path never touches the raster in the browser.
      const isLarge = needsTileGeneration(origWidth, origHeight);
      if (isLarge) {
        const currentSiteId = useProjectStore.getState().meta.siteId;

        if (currentSiteId) {
          // HD path: upload source → server preview (small sources) or
          // placeholder + immediate tile generation (large sources — the
          // synchronous Lambda preview times out on them; the COG converter
          // writes an overview preview that the tile poller adopts on ready).
          try {
            setProgressOverlay({ visible: true, label: `Uploading ${file.name}...`, current: 0, total: file.size });
            const fileExt = file.name.split('.').pop() || 'tif';
            const earlyOrthoId = `ortho_${Date.now()}`;
            const uploadResult = await uploadLargeAsset(
              file, currentSiteId, 'orthophoto_source', `${earlyOrthoId}_source.${fileExt}`,
              (loaded, total) => setProgressOverlay({ visible: true, label: `Uploading ${file.name}...`, current: loaded, total }),
            );
            const hdSourceR2Key = uploadResult.r2Key;

            const skipServerPreview = exceedsServerPreviewLimits(file.size, origWidth, origHeight);
            let previewUrl: string | null = null;
            if (!skipServerPreview) {
              setProgressOverlay({ visible: true, label: 'Generating server preview...', current: 0, total: 0 });
              try {
                const previewResult = await requestPreview(currentSiteId, earlyOrthoId, hdSourceR2Key, removeBgEnabled);
                console.log(`[GeoTIFF] Server preview ready: ${previewResult.width}×${previewResult.height} in ${previewResult.elapsed}s`);
                previewUrl = previewResult.previewUrl;
              } catch (prevErr) {
                // Preview failure must not strand a fully uploaded multi-GB
                // source — fall through to the placeholder + auto-generate flow.
                console.warn('[GeoTIFF] Server preview failed, using placeholder:', prevErr);
              }
            }
            setProgressOverlay(null);

            const usedPlaceholder = !previewUrl;
            if (!previewUrl) previewUrl = makePendingPlaceholder(origWidth, origHeight);

            // Placeholder orthos have nothing to look at or adjust, and the
            // GeoTIFF corners are authoritative — start tile generation NOW so
            // the user isn't left with a "click Generate HD" dead end.
            let tilesetId: string | undefined;
            let tileStatus: 'generating' | undefined;
            if (usedPlaceholder) {
              try {
                const gen = await requestTileGeneration(
                  currentSiteId, 'orthophoto', earlyOrthoId, hdSourceR2Key,
                  { minLng: boundsMinLng, minLat: boundsMinLat, maxLng: boundsMaxLng, maxLat: boundsMaxLat },
                  corners.map((c) => [c[0], c[1]]), origWidth, origHeight, removeBgEnabled,
                );
                tilesetId = gen.tilesetId;
                tileStatus = 'generating';
              } catch (genErr) {
                console.error('[GeoTIFF] Auto tile generation failed to start:', genErr);
                addNotification({ type: 'error', title: 'HD generation failed to start', message: `${(genErr as Error).message} — use "Generate HD" to retry.` });
              }
            }

            useProjectStore.getState().addOrthophoto({
              id: earlyOrthoId, name: orthoName,
              url: previewUrl, coordinates: corners,
              bounds: { minLng: boundsMinLng, minLat: boundsMinLat, maxLng: boundsMaxLng, maxLat: boundsMaxLat },
              opacity: 100, visible: true, isManual: false, fileName: file.name, fileSize: file.size,
              width: origWidth, height: origHeight, captureDate: orthoCaptureDate,
              sourceR2Key: hdSourceR2Key, bgRemoved: removeBgEnabled,
              tilesetId, tileStatus,
            });
            if (orthoCaptureDate) useProjectStore.getState().updateAvailableDates();
            // A real preview supports the adjust-then-generate flow; a
            // placeholder has nothing to align, so skip edit mode.
            if (!usedPlaceholder) useMapStore.getState().enterOrthoEditMode(earlyOrthoId);
            const map = useMapStore.getState().map;
            if (map) map.fitBounds([[boundsMinLng, boundsMinLat], [boundsMaxLng, boundsMaxLat]], { padding: 60, duration: 1500 });
            addNotification({
              type: 'success', title: 'Orthophoto added (HD)',
              message: usedPlaceholder
                ? `${file.name} — HD tiles are generating at the georeferenced position; the preview appears when they're ready.`
                : `${file.name} — adjust position, then "Generate HD" for full-res tiles.`,
            });
            return;
          } catch (hdErr) {
            setProgressOverlay(null);
            console.error('[GeoTIFF] HD upload/preview failed:', hdErr);
            addNotification({ type: 'error', title: 'HD upload failed', message: (hdErr as Error).message });
            return;
          }
        }
        // No siteId (unsaved project) — R2 upload is impossible, so the
        // browser decode below is the only physically available path. Say why
        // rather than silently degrading. Refuse sizes known to kill the tab
        // (readRasters ArrayBuffer allocation) instead of letting it die.
        addNotification({
          type: 'info', title: 'Browser preview only',
          message: 'Save the project to the server to get full-resolution HD tiles for large orthophotos.',
        });
        if (file.size > FAST_DECODE_MAX_BYTES) {
          addNotification({
            type: 'error', title: 'Too large for browser preview',
            message: `${(file.size / 1e9).toFixed(1)} GB exceeds the browser decode limit (${(FAST_DECODE_MAX_BYTES / 1e9).toFixed(0)} GB). Save the project to the server first.`,
          });
          return;
        }
      }

      // ── Local decode path (small files, or explicit Fast choice) ──
      const MAX_DIM = 8192;
      let renderWidth = origWidth, renderHeight = origHeight;
      if (origWidth > MAX_DIM || origHeight > MAX_DIM) {
        const scale = Math.min(MAX_DIM / origWidth, MAX_DIM / origHeight);
        renderWidth = Math.round(origWidth * scale);
        renderHeight = Math.round(origHeight * scale);
        addNotification({ type: 'info', title: 'Downsampling...', message: `${origWidth}×${origHeight} → ${renderWidth}×${renderHeight}` });
      }
      addNotification({ type: 'info', title: 'Reading raster data...' });
      const rasters = await image.readRasters({ width: renderWidth, height: renderHeight });
      addNotification({ type: 'info', title: 'Rendering image...' });
      const canvas = document.createElement('canvas');
      canvas.width = renderWidth;
      canvas.height = renderHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Failed to get canvas context');
      const imageData = ctx.createImageData(renderWidth, renderHeight);
      const px = imageData.data;
      const numBands = rasters.length;
      const totalPixels = renderWidth * renderHeight;
      if (numBands >= 3) {
        for (let i = 0; i < totalPixels; i++) {
          px[i * 4] = (rasters[0] as unknown as Uint8Array)[i];
          px[i * 4 + 1] = (rasters[1] as unknown as Uint8Array)[i];
          px[i * 4 + 2] = (rasters[2] as unknown as Uint8Array)[i];
          px[i * 4 + 3] = numBands >= 4 ? (rasters[3] as unknown as Uint8Array)[i] : 255;
        }
      } else if (numBands === 1) {
        for (let i = 0; i < totalPixels; i++) {
          const val = (rasters[0] as unknown as Uint8Array)[i];
          px[i * 4] = val; px[i * 4 + 1] = val; px[i * 4 + 2] = val; px[i * 4 + 3] = 255;
        }
      }
      ctx.putImageData(imageData, 0, 0);
      if (removeBgEnabled) {
        const bgData = ctx.getImageData(0, 0, renderWidth, renderHeight);
        floodFillRemoveBackground(bgData.data, renderWidth, renderHeight);
        ctx.putImageData(bgData, 0, 0);
      }
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
      if (!blob) throw new Error('Failed to create PNG from raster data');
      // Use blob URL for instant local display (uploaded on explicit "Save to Server")
      const imageUrl = URL.createObjectURL(blob);
      const orthoId = `ortho_${Date.now()}`;

      useProjectStore.getState().addOrthophoto({
        id: orthoId, name: orthoName,
        url: imageUrl, coordinates: corners,
        bounds: { minLng: boundsMinLng, minLat: boundsMinLat, maxLng: boundsMaxLng, maxLat: boundsMaxLat },
        opacity: 100, visible: true, isManual: false, fileName: file.name, fileSize: file.size,
        width: origWidth, height: origHeight, captureDate: orthoCaptureDate,
        bgRemoved: removeBgEnabled,
      });

      // Store original source file for tile generation if image is large (fast path)
      if (isLarge) {
        storeSourceFile(orthoId, file, origWidth, origHeight);
        console.log(`[EditorPanel] Stored source file for tile gen: ${file.name} (${origWidth}×${origHeight} = ${(origWidth * origHeight / 1e6).toFixed(1)}M pixels)`);
      }

      if (orthoCaptureDate) useProjectStore.getState().updateAvailableDates();
      useMapStore.getState().enterOrthoEditMode(orthoId);
      const map = useMapStore.getState().map;
      if (map) map.fitBounds([[boundsMinLng, boundsMinLat], [boundsMaxLng, boundsMaxLat]], { padding: 60, duration: 1500 });
      addNotification({ type: 'success', title: 'Orthophoto added', message: `${file.name} (${origWidth}×${origHeight}px)` });
    } catch (error) {
      console.error('GeoTIFF processing error:', error);
      addNotification({ type: 'error', title: 'Failed to load GeoTIFF', message: error instanceof Error ? error.message : 'Unknown error' });
    }
  };

  const handleOrthophotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await processGeoTIFFFile(file);
    e.target.value = '';
  };

  const handleGeotiffDragOver = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.classList.add('orthophoto-dropzone--dragover'); };
  const handleGeotiffDragLeave = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.classList.remove('orthophoto-dropzone--dragover'); };
  const handleGeotiffDrop = async (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    e.currentTarget.classList.remove('orthophoto-dropzone--dragover');
    for (const file of Array.from(e.dataTransfer.files)) {
      const ext = file.name.toLowerCase().split('.').pop() || '';
      if (['tif', 'tiff', 'geotiff'].includes(ext) || file.type === 'image/tiff') await processGeoTIFFFile(file);
      else addNotification({ type: 'error', title: 'Invalid file', message: `${file.name} is not a GeoTIFF` });
    }
  };

  const processManualImageFile = async (file: File) => {
    const validExts = ['jpg', 'jpeg', 'png', 'gif'];
    const ext = file.name.toLowerCase().split('.').pop() || '';
    if (!validExts.includes(ext)) {
      addNotification({ type: 'error', title: 'Invalid file type', message: 'Please select a JPG, PNG, or GIF image' });
      return;
    }
    addNotification({ type: 'info', title: 'Loading image...', message: file.name });
    try {
      // Read image dimensions from file header (1MB scan — large JPEGs have huge EXIF/ICC data)
      const headerSize = Math.min(file.size, 1048576); // 1MB
      const headerBuf = new Uint8Array(await file.slice(0, headerSize).arrayBuffer());
      let width = 0, height = 0;
      if (headerBuf[0] === 0x89 && headerBuf[1] === 0x50) { // PNG
        width = (headerBuf[16] << 24) | (headerBuf[17] << 16) | (headerBuf[18] << 8) | headerBuf[19];
        height = (headerBuf[20] << 24) | (headerBuf[21] << 16) | (headerBuf[22] << 8) | headerBuf[23];
      } else if (headerBuf[0] === 0x47 && headerBuf[1] === 0x49) { // GIF
        width = headerBuf[6] | (headerBuf[7] << 8);
        height = headerBuf[8] | (headerBuf[9] << 8);
      } else if (headerBuf[0] === 0xFF && headerBuf[1] === 0xD8) { // JPEG — scan for SOF marker
        for (let i = 2; i < headerBuf.length - 8;) {
          if (headerBuf[i] !== 0xFF) { i++; continue; }
          const marker = headerBuf[i + 1];
          if (marker >= 0xC0 && marker <= 0xCF && marker !== 0xC4 && marker !== 0xC8 && marker !== 0xCC) {
            height = (headerBuf[i + 5] << 8) | headerBuf[i + 6];
            width = (headerBuf[i + 7] << 8) | headerBuf[i + 8];
            break;
          }
          const segLen = (headerBuf[i + 2] << 8) | headerBuf[i + 3];
          if (segLen < 2) break; // corrupt marker
          i += 2 + segLen;
        }
      }

      // Fallback: use createImageBitmap probe to get dimensions
      if (!width || !height) {
        console.log('[ManualImage] Header parse failed, probing with createImageBitmap...');
        const probeBitmap = await createImageBitmap(file);
        width = probeBitmap.width;
        height = probeBitmap.height;
        probeBitmap.close();
      }
      console.log('[ManualImage] Dimensions:', width, '×', height, 'File:', (file.size / 1048576).toFixed(1), 'MB');

      // GPU probe: test actual VRAM capacity by creating textures of increasing size.
      // Uses null data (VRAM-only allocation, no CPU buffer needed).
      // Cached after first probe so subsequent loads don't re-test.
      let maxDim = 4096;
      let maxPixelArea = 16_777_216;
      try {
        const mapCanvas = useMapStore.getState().map?.getCanvas();
        const gl = mapCanvas?.getContext('webgl2') || mapCanvas?.getContext('webgl');
        if (gl) {
          const gpuMax = gl.getParameter(gl.MAX_TEXTURE_SIZE) as number;
          maxDim = Math.min(gpuMax, 16384);

          // Check if we already probed this session
          const cached = (window as any).__gpuMaxPixelArea;
          if (cached) {
            maxPixelArea = cached;
          } else {
            // Probe increasing texture sizes using null data (no CPU allocation)
            const testSizes = [
              [4096, 4096],   // 16.8M
              [5120, 5120],   // 26.2M
              [6144, 6144],   // 37.7M
              [7168, 7168],   // 51.4M
              [8192, 8192],   // 67.1M
              [8192, 10240],  // 83.9M
              [8192, 12288],  // 100.7M
              [8192, 14336],  // 117.4M
              [8192, 16384],  // 134.2M
            ].filter(([w, h]) => w <= gpuMax && h <= gpuMax);

            let bestArea = 16_777_216; // known-good baseline
            for (const [tw, th] of testSizes) {
              const tex = gl.createTexture();
              gl.bindTexture(gl.TEXTURE_2D, tex);
              gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, tw, th, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
              const err = gl.getError();
              gl.deleteTexture(tex);
              if (err === gl.NO_ERROR) {
                bestArea = tw * th;
                console.log(`[GPU Probe] ${tw}×${th} (${(tw * th / 1e6).toFixed(1)}M px) ✓`);
              } else {
                console.log(`[GPU Probe] ${tw}×${th} (${(tw * th / 1e6).toFixed(1)}M px) ✗ error=${err}`);
                break;
              }
            }
            // Use 35% of probed max — null-data probe only tests allocation, not real
            // upload alongside Mapbox's own map tile textures. Hard cap at 50M pixels
            // (200MB RGBA) to stay safe across GPUs with varying VRAM.
            maxPixelArea = Math.min(Math.round(bestArea * 0.35), 50_000_000);
            (window as any).__gpuMaxPixelArea = maxPixelArea;
            console.log(`[GPU Probe] Max safe pixel area: ${(maxPixelArea / 1e6).toFixed(1)}M pixels (${(maxPixelArea * 4 / 1048576).toFixed(0)}MB RGBA)`);
          }
        }
      } catch (_e) { /* fallback */ }

      let renderWidth = width, renderHeight = height;
      if (width > maxDim || height > maxDim || width * height > maxPixelArea) {
        let scale = Math.min(maxDim / width, maxDim / height);
        if (width * scale * height * scale > maxPixelArea) {
          scale = Math.sqrt(maxPixelArea / (width * height));
        }
        renderWidth = Math.round(width * scale);
        renderHeight = Math.round(height * scale);
        addNotification({ type: 'info', title: 'Downsampling...', message: `${width}×${height} → ${renderWidth}×${renderHeight}` });
      }

      // ── Large manual images default straight to HD — no chooser (owner
      // decision 2026-09-01; see the GeoTIFF branch above for the rationale).
      if (needsTileGeneration(width, height)) {
        const currentSiteId = useProjectStore.getState().meta.siteId;

        if (currentSiteId) {
          // HD path: upload source via multipart → Lambda preview (small
          // sources) or placeholder (large — the synchronous preview times out;
          // the COG converter's overview preview is adopted after Generate HD).
          // No auto-generation here: a manual image must be hand-placed first,
          // or the tiles bake at the arbitrary center-screen position.
          try {
            setProgressOverlay({ visible: true, label: `Uploading ${file.name}...`, current: 0, total: file.size });
            const fileExt = file.name.split('.').pop() || 'jpg';
            const earlyOrthoId = `ortho_manual_${Date.now()}`;
            const uploadResult = await uploadLargeAsset(
              file, currentSiteId, 'orthophoto_source', `${earlyOrthoId}_source.${fileExt}`,
              (loaded, total) => {
                setProgressOverlay({ visible: true, label: `Uploading ${file.name}...`, current: loaded, total });
              },
            );
            const hdSourceR2Key = uploadResult.r2Key;

            let hdPreviewUrl: string | null = null;
            if (!exceedsServerPreviewLimits(file.size, width, height)) {
              setProgressOverlay({ visible: true, label: 'Generating server preview...', current: 0, total: 0 });
              try {
                const previewResult = await requestPreview(currentSiteId, earlyOrthoId, hdSourceR2Key, removeBgEnabled);
                console.log(`[ManualImage] Server preview ready: ${previewResult.width}×${previewResult.height} in ${previewResult.elapsed}s`);
                hdPreviewUrl = previewResult.previewUrl;
              } catch (prevErr) {
                console.warn('[ManualImage] Server preview failed, using placeholder:', prevErr);
              }
            }
            setProgressOverlay(null);
            if (!hdPreviewUrl) hdPreviewUrl = makePendingPlaceholder(width, height);

            // Add to map directly using server preview URL
            const orthoId = earlyOrthoId;
            const defaultBounds = useMapStore.getState().map!.getBounds()!;
            const mapCenter = defaultBounds.getCenter();
            const spanLng = (defaultBounds.getEast() - defaultBounds.getWest()) * 0.3;
            const spanLat = spanLng * (height / width);
            const coordinates: [[number, number], [number, number], [number, number], [number, number]] = [
              [mapCenter.lng - spanLng / 2, mapCenter.lat + spanLat / 2],
              [mapCenter.lng + spanLng / 2, mapCenter.lat + spanLat / 2],
              [mapCenter.lng + spanLng / 2, mapCenter.lat - spanLat / 2],
              [mapCenter.lng - spanLng / 2, mapCenter.lat - spanLat / 2],
            ];
            const lngs = coordinates.map((c) => c[0]);
            const lats = coordinates.map((c) => c[1]);

            useProjectStore.getState().addOrthophoto({
              id: orthoId,
              name: file.name,
              url: hdPreviewUrl,
              coordinates,
              bounds: { minLng: Math.min(...lngs), maxLng: Math.max(...lngs), minLat: Math.min(...lats), maxLat: Math.max(...lats) },
              opacity: 100,
              visible: true,
              isManual: true,
              fileSize: file.size,
              width,
              height,
              sourceR2Key: hdSourceR2Key,
              bgRemoved: removeBgEnabled,
            });
            useMapStore.getState().enterOrthoEditMode(orthoId);
            addNotification({ type: 'success', title: 'Image added', message: 'Adjust position, then click "Generate HD" for full resolution tiles.' });
            return; // Done — skip the local decode path entirely
          } catch (hdErr) {
            setProgressOverlay(null);
            console.error('[ManualImage] HD upload/preview failed:', hdErr);
            addNotification({ type: 'error', title: 'HD upload failed', message: (hdErr as Error).message });
            return;
          }
        }
        // No siteId (unsaved project) — R2 upload is impossible; the local
        // decode below is the only available path.
        addNotification({
          type: 'info', title: 'Browser preview only',
          message: 'Save the project to the server to get full-resolution HD tiles for large images.',
        });
      }

      // Browser bitmap limit is ~268M pixels; images beyond that need native decode
      const BROWSER_PIXEL_LIMIT = 268_000_000;
      const electronAPI = (window as any).electronAPI;
      const needsNativeDecode = width * height > BROWSER_PIXEL_LIMIT;

      // Track whether sharp produced the final blob directly (avoids double compression)
      let directBlob: Blob | null = null;
      // Server-side preview: when browser can't decode and we have a siteId,
      // upload source to R2 and generate a ~2000px preview via Lambda.
      let serverPreviewUrl: string | null = null;
      let serverSourceR2Key: string | null = null;

      const canvas = document.createElement('canvas');
      canvas.width = renderWidth; canvas.height = renderHeight;
      const ctx = canvas.getContext('2d')!;

      if (!needsNativeDecode) {
        // Standard browser decode (< 268M pixels)
        const needsResize = renderWidth !== width || renderHeight !== height;
        const bitmapOpts = needsResize
          ? { resizeWidth: renderWidth, resizeHeight: renderHeight, resizeQuality: 'high' as ImageBitmapOptions['resizeQuality'] }
          : {};
        const bitmap = await createImageBitmap(file, bitmapOpts);
        ctx.drawImage(bitmap, 0, 0, renderWidth, renderHeight);
        bitmap.close();
      } else {
        // Large image (> 268M pixels) — try multiple decode strategies in sequence
        let decoded = false;

        // Strategy 1: Electron sharp via IPC (tiled streaming — handles any size)
        if (electronAPI?.resizeImage) {
          try {
            addNotification({ type: 'info', title: 'Processing large image...', message: `${width}×${height} via native decoder` });
            const ab = await file.arrayBuffer();
            const result = await electronAPI.resizeImage(ab, renderWidth, renderHeight);
            const resultMime = result.mimeType || 'image/jpeg';
            renderWidth = result.width || renderWidth;
            renderHeight = result.height || renderHeight;

            if (!removeBgEnabled) {
              // Use sharp output directly — single encode, no double compression
              directBlob = new Blob([result.buffer], { type: resultMime });
            } else {
              // Need canvas for flood fill — draw sharp output to canvas
              canvas.width = renderWidth; canvas.height = renderHeight;
              const resizedBlob = new Blob([result.buffer], { type: resultMime });
              const bitmap = await createImageBitmap(resizedBlob);
              ctx.drawImage(bitmap, 0, 0);
              bitmap.close();
            }
            decoded = true;
          } catch (e) {
            console.warn('[ManualImage] Electron sharp failed, trying next strategy:', e);
          }
        }

        // Strategy 2: createImageBitmap with resize — Chrome can IDCT-downscale JPEG
        // directly to the target size, sometimes avoiding the full pixel buffer limit
        if (!decoded) {
          try {
            addNotification({ type: 'info', title: 'Decoding large image...', message: `${width}×${height} → ${renderWidth}×${renderHeight}` });
            const bitmap = await createImageBitmap(file, {
              resizeWidth: renderWidth, resizeHeight: renderHeight,
              resizeQuality: 'high',
            });
            ctx.drawImage(bitmap, 0, 0);
            bitmap.close();
            decoded = true;
          } catch (e) {
            console.warn('[ManualImage] createImageBitmap with resize failed:', e);
          }
        }

        // Strategy 3: WebCodecs ImageDecoder (Chromium — YCbCr decode, ~60% less memory)
        if (!decoded && 'ImageDecoder' in window) {
          try {
            const decoder = new (window as any).ImageDecoder({ type: file.type || 'image/jpeg', data: file.stream() });
            await decoder.completed;
            const { image: frame } = await decoder.decode();
            ctx.drawImage(frame, 0, 0, renderWidth, renderHeight);
            frame.close();
            decoder.close();
            decoded = true;
          } catch (e) {
            console.warn('[ManualImage] ImageDecoder also failed:', e);
          }
        }

        if (!decoded) {
          const currentSiteId = useProjectStore.getState().meta.siteId;
          // Over the preview limits the synchronous Lambda preview would just
          // time out after minutes — skip straight to the placeholder path.
          if (needsTileGeneration(width, height) && currentSiteId
              && !exceedsServerPreviewLimits(file.size, width, height)) {
            // Server-side preview generation
            try {
              addNotification({ type: 'info', title: 'Uploading image to server...', message: `${file.name} (${(file.size / 1048576).toFixed(1)}MB)` });
              const ext = file.name.split('.').pop() || 'jpg';
              const earlyOrthoId = `ortho_manual_${Date.now()}`;
              const uploadResult = await uploadAsset(file, currentSiteId, 'orthophoto_source', `${earlyOrthoId}_source.${ext}`);
              serverSourceR2Key = uploadResult.r2Key;

              addNotification({ type: 'info', title: 'Generating server preview...', message: `${width}×${height} → ~2000px` });
              const previewResult = await requestPreview(currentSiteId, earlyOrthoId, uploadResult.r2Key, removeBgEnabled);
              serverPreviewUrl = previewResult.previewUrl;
              console.log(`[ManualImage] Server preview ready: ${previewResult.width}×${previewResult.height} in ${previewResult.elapsed}s`);
              decoded = true;
            } catch (previewErr) {
              console.warn('[ManualImage] Server preview failed, falling back to placeholder:', previewErr);
              serverPreviewUrl = null;
              // serverSourceR2Key is deliberately KEPT. The source is already
              // uploaded by this point (see above), so clearing it dropped the
              // only reference to a multi-hundred-MB R2 object AND threw away
              // the HD-source wiring — the ortho then had no path to tiles at
              // all. Harmless while the preview almost always succeeded; once
              // the AWS preview Lambda goes away this becomes the normal path,
              // leaking on every large manual-image upload.
            }
          }

          if (!decoded) {
            if (needsTileGeneration(width, height)) {
              // No siteId or preview failed — show placeholder
              addNotification({ type: 'info', title: 'Image too large for browser preview', message: 'Save project to server to enable preview generation' });
              canvas.width = 512; canvas.height = Math.round(512 * (height / width));
              const pCtx = canvas.getContext('2d')!;
              pCtx.fillStyle = '#1a1a2e';
              pCtx.fillRect(0, 0, canvas.width, canvas.height);
              pCtx.fillStyle = '#e0e0e0';
              pCtx.font = 'bold 24px sans-serif';
              pCtx.textAlign = 'center';
              pCtx.fillText('HD tiles pending...', canvas.width / 2, canvas.height / 2 - 10);
              pCtx.font = '14px sans-serif';
              pCtx.fillText(`${width}×${height} px`, canvas.width / 2, canvas.height / 2 + 20);
              renderWidth = canvas.width;
              renderHeight = canvas.height;
              decoded = true;
            } else {
              throw new Error(`Image too large for browser (${width}×${height} = ${(width * height / 1e6).toFixed(0)}M pixels). Use the desktop app or pre-resize to under ${maxDim}×${maxDim} pixels.`);
            }
          }
        }
      }

      let imageUrl: string;
      if (serverPreviewUrl) {
        // Server-generated preview — use the R2 URL directly
        canvas.width = 0; canvas.height = 0;
        imageUrl = serverPreviewUrl;
      } else if (directBlob) {
        // Sharp already produced the final output — no re-encoding needed
        canvas.width = 0; canvas.height = 0;
        imageUrl = URL.createObjectURL(directBlob);
      } else {
        // Canvas path — apply bg removal if enabled, then encode once
        if (removeBgEnabled) {
          const imageData = ctx.getImageData(0, 0, renderWidth, renderHeight);
          floodFillRemoveBackground(imageData.data, renderWidth, renderHeight);
          ctx.putImageData(imageData, 0, 0);
        }
        // Detect grayscale by sampling canvas pixels — thermal images should stay lossless
        let isGrayscale = false;
        if (!removeBgEnabled) {
          const probe = ctx.getImageData(0, 0, Math.min(renderWidth, 100), Math.min(renderHeight, 100));
          const px = probe.data;
          let monoCount = 0;
          for (let i = 0; i < px.length; i += 16) { // sample every 4th pixel
            if (px[i] === px[i + 1] && px[i + 1] === px[i + 2]) monoCount++;
          }
          isGrayscale = monoCount / (px.length / 16) > 0.95;
        }
        // PNG for bg removal (needs alpha) or grayscale (lossless thermal data)
        // High-quality JPEG for RGB visual images
        const mimeType = (removeBgEnabled || isGrayscale) ? 'image/png' : 'image/jpeg';
        const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, mimeType, 0.95));
        canvas.width = 0; canvas.height = 0; // Release GPU memory
        if (!blob) throw new Error('Failed to create image from canvas');
        imageUrl = URL.createObjectURL(blob);
        if (isGrayscale) console.log('[ManualImage] Detected grayscale — using lossless PNG');
      }

      // Use blob URL for instant local display (uploaded on explicit "Save to Server")
      const map = useMapStore.getState().map;
      const center = map ? map.getCenter() : { lng: useMapStore.getState().center[0], lat: useMapStore.getState().center[1] };
      const targetWidthMeters = 200;
      const aspectRatio = height / width;
      const latOffset = (targetWidthMeters * aspectRatio) / 111320;
      const lngOffset = targetWidthMeters / (111320 * Math.cos(center.lat * Math.PI / 180));
      const coordinates: [[number, number], [number, number], [number, number], [number, number]] = [
        [center.lng - lngOffset / 2, center.lat + latOffset / 2],
        [center.lng + lngOffset / 2, center.lat + latOffset / 2],
        [center.lng + lngOffset / 2, center.lat - latOffset / 2],
        [center.lng - lngOffset / 2, center.lat - latOffset / 2],
      ];
      const orthoId = `ortho_manual_${Date.now()}`;
      const manualCaptureDate = useProjectStore.getState().selectedDate || undefined;
      useProjectStore.getState().addOrthophoto({
        id: orthoId, name: file.name.replace(/\.(jpg|jpeg|png|gif)$/i, ''),
        url: imageUrl, coordinates,
        bounds: { minLng: center.lng - lngOffset / 2, maxLng: center.lng + lngOffset / 2, minLat: center.lat - latOffset / 2, maxLat: center.lat + latOffset / 2 },
        opacity: 100, visible: true, isManual: true, fileName: file.name, fileSize: file.size, width, height, captureDate: manualCaptureDate,
        bgRemoved: removeBgEnabled,
        sourceR2Key: serverSourceR2Key || undefined,
      });

      // Store original source file for tile generation if image is large
      // Skip if source was already uploaded to R2 during server preview
      if (needsTileGeneration(width, height) && !serverSourceR2Key) {
        storeSourceFile(orthoId, file, width, height);
        console.log(`[EditorPanel] Stored source file for tile gen: ${file.name} (${width}×${height} = ${(width * height / 1e6).toFixed(1)}M pixels)`);
      }

      if (manualCaptureDate) useProjectStore.getState().updateAvailableDates();
      useMapStore.getState().enterOrthoEditMode(orthoId);
      addNotification({ type: 'success', title: 'Manual image added', message: `${file.name} (${width}×${height}px)` });
    } catch (error) {
      console.error('Manual image error:', error);
      addNotification({ type: 'error', title: 'Failed to load image', message: error instanceof Error ? error.message : 'Unknown error' });
    }
  };

  const handleManualImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await processManualImageFile(file);
    e.target.value = '';
  };

  const handleManualDragOver = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.classList.add('orthophoto-dropzone--dragover'); };
  const handleManualDragLeave = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.classList.remove('orthophoto-dropzone--dragover'); };
  const handleManualDrop = async (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    e.currentTarget.classList.remove('orthophoto-dropzone--dragover');
    for (const file of Array.from(e.dataTransfer.files)) {
      const ext = file.name.toLowerCase().split('.').pop() || '';
      if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) await processManualImageFile(file);
      else addNotification({ type: 'error', title: 'Invalid file', message: `${file.name} is not a JPG/PNG/GIF` });
    }
  };

  const floodFillRemoveBackground = (px: Uint8ClampedArray, w: number, h: number) => {
    const isBg = (idx: number) => {
      const r = px[idx], g = px[idx + 1], b = px[idx + 2];
      return (r <= 15 && g <= 15 && b <= 15) || (r >= 240 && g >= 240 && b >= 240);
    };
    const visited = new Uint8Array(w * h);
    const queue: number[] = [];
    for (let x = 0; x < w; x++) {
      if (isBg(x * 4)) { queue.push(x); visited[x] = 1; }
      const bIdx = (h - 1) * w + x;
      if (isBg(bIdx * 4)) { queue.push(bIdx); visited[bIdx] = 1; }
    }
    for (let y = 1; y < h - 1; y++) {
      const lIdx = y * w;
      if (isBg(lIdx * 4)) { queue.push(lIdx); visited[lIdx] = 1; }
      const rIdx = y * w + (w - 1);
      if (isBg(rIdx * 4)) { queue.push(rIdx); visited[rIdx] = 1; }
    }
    let head = 0;
    while (head < queue.length) {
      const pixelIdx = queue[head++];
      const x = pixelIdx % w;
      const y = (pixelIdx - x) / w;
      px[pixelIdx * 4 + 3] = 0;
      const neighbors = [y > 0 ? pixelIdx - w : -1, y < h - 1 ? pixelIdx + w : -1, x > 0 ? pixelIdx - 1 : -1, x < w - 1 ? pixelIdx + 1 : -1];
      for (const nIdx of neighbors) {
        if (nIdx >= 0 && !visited[nIdx] && isBg(nIdx * 4)) { visited[nIdx] = 1; queue.push(nIdx); }
      }
    }
  };

  const handleOrthoZoom = (orthoId: string) => {
    const ortho = orthophotos.find((o) => o.id === orthoId);
    if (!ortho || !mapInstance) return;
    const coords = ortho.coordinates;
    if (coords?.length >= 3) {
      const lngs = coords.map((c: number[]) => c[0]);
      const lats = coords.map((c: number[]) => c[1]);
      mapInstance.fitBounds([[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]], { padding: 60, duration: 1500 });
    }
  };

  const handleOrthoRemove = (orthoId: string) => {
    const ortho = orthophotos.find((o) => o.id === orthoId);
    if (!ortho) return;
    showConfirm({
      title: 'Remove Orthophoto', message: `Remove "${ortho.name}"? This cannot be undone.`,
      variant: 'danger', confirmLabel: 'Remove',
      onConfirm: () => {
        if (ortho.url.startsWith('blob:')) URL.revokeObjectURL(ortho.url);
        // Clear active selection first so UI updates cleanly
        if (useMapStore.getState().activeOrthophotoId === orthoId) {
          const remaining = useProjectStore.getState().data.orthophotos.filter((o) => o.id !== orthoId);
          useMapStore.getState().setActiveOrthophoto(remaining.length > 0 ? remaining[0].id : null);
        }
        useProjectStore.getState().removeOrthophoto(orthoId);
        addNotification({ type: 'info', title: 'Orthophoto removed' });
      },
    });
  };

  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <>
      {/* Hidden file inputs for orthophoto uploads */}
      <input ref={orthophotoInputRef} type="file" accept=".tif,.tiff,.geotiff,image/tiff" onChange={handleOrthophotoSelect} style={{ display: 'none' }} />
      <input ref={manualImageInputRef} type="file" accept=".jpg,.jpeg,.png,.gif,image/jpeg,image/png,image/gif" onChange={handleManualImageSelect} style={{ display: 'none' }} />

      <div className="ep-ticket-content" data-testid="orthophoto-ticket">
        {/* F-02E-2B: share viewers see no upload dropzones or BG-remove
            settings — these all trigger uploadAsset/import writes. */}
        {!isReadOnly && (
          <>
            <div className="ep-section-label">GeoTIFF</div>
            <div
              className="orthophoto-dropzone"
              onClick={handleAddOrthophoto}
              onDragOver={handleGeotiffDragOver}
              onDragLeave={handleGeotiffDragLeave}
              onDrop={handleGeotiffDrop}
              data-testid="orthophoto-ticket-upload-geotiff"
            >
              <span>Drop GeoTIFF or click</span>
            </div>
            <div className="ep-divider" />
            <div className="ep-section-label">Manual Image</div>
            <div
              className="orthophoto-dropzone orthophoto-dropzone--manual"
              onClick={handleAddManualImage}
              onDragOver={handleManualDragOver}
              onDragLeave={handleManualDragLeave}
              onDrop={handleManualDrop}
              data-testid="orthophoto-ticket-upload-manual"
            >
              <span>Drop JPG / PNG / GIF or click</span>
            </div>
            <label className="ep-checkbox-row">
              <input type="checkbox" checked={removeBgEnabled} onChange={(e) => setRemoveBgEnabled(e.target.checked)} />
              <span>Remove background (next upload)</span>
            </label>
          </>
        )}

        {orthophotos.length > 0 && (
          <>
            <div className="ep-divider" />
            <div className="ep-section-label">Layers ({orthophotos.length})</div>
            {orthophotos.some((o) => o.tileStatus === 'generating') && (
              <div className="ortho-generating-banner">
                <span className="ortho-generating-spinner" />
                Generating HD tiles... This may take a few minutes.
              </div>
            )}
            <div className="ortho-list">
              {orthophotos.map((ortho, index) => {
                const isActive = activeOrthophotoId === ortho.id;
                return (
                  <div key={ortho.id} className={`ortho-item${isActive ? ' ortho-item--active' : ''}${ortho.isManual ? ' ortho-item--manual' : ''}`} onClick={() => useMapStore.getState().setActiveOrthophoto(ortho.id)}>
                    <input type="checkbox" checked={ortho.visible} onChange={(ev) => { ev.stopPropagation(); useProjectStore.getState().toggleOrthophotoVisibility(ortho.id); }} title="Toggle visibility" className="ortho-item__checkbox" />
                    <div className="ortho-item__info">
                      <div className="ortho-item__name">
                        {ortho.name}
                        {ortho.isManual && <span className="ortho-badge">manual</span>}
                        {ortho.tileStatus === 'ready' && <span className="ortho-badge ortho-badge--hd" title="Full resolution tiles">HD</span>}
                        {ortho.tileStatus === 'generating' && <span className="ortho-badge ortho-badge--pending" title="Generating tiles...">...</span>}
                        {ortho.tileStatus === 'error' && <span className="ortho-badge ortho-badge--error" title="Tile generation failed">!</span>}
                      </div>
                      {ortho.fileSize && <div className="ortho-item__size">{formatFileSize(ortho.fileSize)}</div>}
                    </div>
                    {/* Reorder: top of list = top layer. Hidden for share viewers
                        and when there's nothing to reorder. */}
                    {!isReadOnly && orthophotos.length > 1 && (
                      <div className="ortho-item__reorder">
                        <button
                          type="button"
                          className="ortho-reorder-btn"
                          title="Move up (toward top)"
                          disabled={index === 0}
                          onClick={(ev) => { ev.stopPropagation(); useProjectStore.getState().moveOrthophoto(ortho.id, 'up'); }}
                          data-testid={`orthophoto-move-up-${ortho.id}`}
                        >
                          &#x25B2;
                        </button>
                        <button
                          type="button"
                          className="ortho-reorder-btn"
                          title="Move down (toward bottom)"
                          disabled={index === orthophotos.length - 1}
                          onClick={(ev) => { ev.stopPropagation(); useProjectStore.getState().moveOrthophoto(ortho.id, 'down'); }}
                          data-testid={`orthophoto-move-down-${ortho.id}`}
                        >
                          &#x25BC;
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {(() => {
              const activeOrtho = activeOrthophotoId ? orthophotos.find((o) => o.id === activeOrthophotoId) : null;
              if (!activeOrtho) return null;
              return (
                <div className="ortho-controls">
                  <div className="ortho-controls__row">
                    <span className="ortho-controls__label">Opacity</span>
                    <input type="range" min="0" max="100" step="1" value={activeOrtho.opacity} onChange={(ev) => useProjectStore.getState().setOrthophotoOpacity(activeOrtho.id, Number(ev.target.value))} className="ortho-controls__slider" />
                    <span className="ortho-controls__val">{activeOrtho.opacity}%</span>
                  </div>
                  <div className="ortho-controls__actions">
                    {/* Zoom is a pure view control — kept for visitors. */}
                    <Button variant="secondary" size="sm" onClick={() => handleOrthoZoom(activeOrtho.id)}>Zoom</Button>
                    {/* F-02E-2B: Adjust / Regen / Remove are mutations — hidden. */}
                    {!isReadOnly && (
                      <Button variant="secondary" size="sm" onClick={() => useMapStore.getState().enterOrthoEditMode(activeOrtho.id)} data-testid="orthophoto-ticket-adjust">Adjust</Button>
                    )}
                    {/* Regen needs a raw source OR a prior tileset — the server prefers
                        the prior tileset's own COG as the source (survives raw
                        retention deletes; lossless retag fast path) and deletes the
                        old tileset itself when the new one completes. Hidden while
                        generating: one regen in flight per ortho. */}
                    {!isReadOnly && (activeOrtho.sourceR2Key || activeOrtho.tilesetId) && meta.siteId &&
                      activeOrtho.tileStatus !== 'generating' && (
                      <Button variant="secondary" size="sm" onClick={async () => {
                        try {
                          // Issue the regenerate request first, then commit the
                          // resulting tilesetId + tileStatus in a single
                          // updateOrthophoto call. Two back-to-back calls would
                          // race on _version (second PATCH sees stale version
                          // → 412 → server reload leaves tilesetId unset).
                          // NON-DESTRUCTIVE: the old tileset is NOT deleted up
                          // front — the converter reads its COG as the source.
                          // The SERVER deletes it when the new one completes;
                          // on failure the poller rolls back to it.
                          const result = await regenerateTiles(meta.siteId!, activeOrtho);
                          const orthoId = activeOrtho.id;
                          const commitFlip = () => useProjectStore.getState().updateOrthophoto(orthoId, {
                            tilesetId: result.tilesetId,
                            tileStatus: 'generating',
                          });
                          commitFlip();
                          // The entity PATCH behind the flip can lose a _version
                          // race (412 → conflict reload replaces the ortho with
                          // the server copy, wiping it). A lost flip is DANGEROUS
                          // for a regen: the ortho keeps pointing at the PRIOR
                          // tileset, which the server deletes when the new one
                          // completes. Verify after the dust settles and
                          // re-apply (fresh _version) — up to twice.
                          let flipRetries = 0;
                          const verifyFlip = () => {
                            const cur = useProjectStore.getState().data.orthophotos.find((o) => o.id === orthoId);
                            if (!cur || cur.tilesetId === result.tilesetId || flipRetries >= 2) return;
                            flipRetries += 1;
                            commitFlip();
                            setTimeout(verifyFlip, 3000);
                          };
                          setTimeout(verifyFlip, 3000);
                        } catch (err) {
                          addNotification({ type: 'error', title: 'Regeneration failed', message: (err as Error).message });
                        }
                      }} data-testid="orthophoto-ticket-regen">{activeOrtho.tilesetId ? 'Regen HD' : 'Generate HD'}</Button>
                    )}
                    {!isReadOnly && (
                      <Button variant="danger" size="sm" onClick={() => handleOrthoRemove(activeOrtho.id)} data-testid="orthophoto-ticket-remove">Remove</Button>
                    )}
                  </div>
                  {!isReadOnly && (
                    <p className="ortho-controls__hint">Corners: adjust shape · Teal dots: scale · Center: move · Green: rotate</p>
                  )}
                  {!isReadOnly && orthoEditMode && activeOrtho.tileStatus === 'ready' && (
                    <p className="ortho-controls__hint">Showing preview while adjusting — click Regen HD when done to bake tiles at the new position.</p>
                  )}
                </div>
              );
            })()}
          </>
        )}
        {orthophotos.length === 0 && <p className="ep-empty-text">No orthophotos loaded</p>}
      </div>
    </>
  );
}
